import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight } from "lucide-react";
import clsx from "clsx";
import { Button, ConfirmDialog, Dialog, EmptyState, Field, PageHeader, RiskMeter, Skeleton, SourceChip, StatusBadge, TonePill, apiFetch, buttonClass, riskLevelOf, useApi, useCurrentUser, useT, useToast } from "@iga/ui";
import type { Campaign, CampaignProgress, CertItem, Page, Person } from "../lib/types";
import { CERT_KEYS, errMsg, firstName, riskRank, useCampaigns, useFmt } from "../lib/hooks";
import { Avatar, LinkButton, LoadError, NoIdentity, PeoplePicker, PersonLine, ProgressBar } from "../components/common";

type Decision = "keep" | "revoke";
interface PersonGroup { identity: CertItem["identity"]; items: CertItem[]; decided: number }

/** Rule-granted, risk ≤ medium, no open conflict for the person: may be kept in one go (§6 J3). */
const autoKeepable = (it: CertItem) => !it.decision && it.snapshot.subject.source === "rule" && riskRank[it.snapshot.subject.risk ?? "low"] <= 2 && it.identity.openViolations === 0 && !it.snapshot.orphan;

export function ReviewsPage() {
  const t = useT();
  const fmt = useFmt();
  const me = useCurrentUser();
  const res = useCampaigns(!!me?.identity);
  if (!me?.identity) return <><PageHeader portal title={t("p.nav.reviews")} /><NoIdentity /></>;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader portal title={t("p.nav.reviews")} subtitle={t("p.review.listSubtitle")} />
      <LoadError error={res.error} onRetry={() => void res.refetch()} />
      {res.isLoading ? <Skeleton rows={2} variant="card" /> : (res.data?.items.length ?? 0) === 0 ? <EmptyState kind="all-done" title={t("p.review.none")} body={t("p.review.noneBody")} /> : (
        <ul className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {res.data!.items.map((c) => (
            <li key={c.id} className="rounded-card border border-line bg-surface flex flex-col gap-3 p-5">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div><h2 className="text-h2">{c.name}</h2><p className="text-small text-fg-3">{t(`p.review.type.${c.type}`)}{c.asDelegate ? ` · ${t("p.review.asDelegate")}` : ""}</p></div>
                <TonePill tone={c.overdue ? "warn" : c.pending === 0 ? "ok" : "info"}>{c.overdue ? t("p.tasks.overdueShort") : t("p.tasks.dueShort", { when: fmt.day(c.dueAt) })}</TonePill>
              </div>
              <ProgressBar value={c.decided} max={c.total} label={c.name} />
              <p className="text-fg-2">{t("p.review.progress", { decided: c.decided, total: c.total })}{c.pending > 0 ? ` · ${t("p.review.left", { count: c.pending })}` : ""}</p>
              <div><LinkButton to={`/review/${c.id}`} variant={c.pending > 0 ? "primary" : "secondary"}>{c.pending > 0 ? t("p.review.continue") : t("p.review.view")}</LinkButton></div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ one campaign, person by person */

function ItemCard({ it, busy, onDecide, onReassign }: { it: CertItem; busy: boolean; onDecide: (d: Decision) => void; onReassign: () => void }) {
  const t = useT();
  const fmt = useFmt();
  const s = it.snapshot.subject;
  const where = s.application ?? s.system ?? "";
  const how = s.source ? <><SourceChip source={s.source} />{s.validTo && <span className="text-small text-fg-3">{t("p.access.until", { date: fmt.day(s.validTo) })}</span>}</>
    : s.kind === "account" ? <><SourceChip source="external">{t("p.review.account")}</SourceChip>{s.status && <StatusBadge domain="account" status={s.status === "enabled" ? "present" : s.status} compact />}</>
    : <SourceChip source="external" />;
  const pressed = (d: Decision) => it.decision === d;
  return (
    <li className="rounded-card border border-line bg-surface flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h3 className="text-h3">{s.displayName}</h3>
        <span className="text-small text-fg-3">{where}{s.account ? ` · ${s.account}` : ""}{s.kind === "account" && s.entitlementCount !== undefined ? ` · ${t("p.review.entitlements", { count: s.entitlementCount })}` : ""}</span>
        {it.snapshot.orphan && <TonePill tone="warn" compact>{t("status.account.orphan.admin")}</TonePill>}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="flex flex-wrap items-center gap-2">{how}</span>
        {s.risk && <RiskMeter level={riskLevelOf(s.risk)} />}
        {it.identity.openViolations > 0 && <TonePill tone="warn" compact>{t("p.review.conflict", { count: it.identity.openViolations })}</TonePill>}
      </div>
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t("p.review.decisionFor", { item: s.displayName })}>
        <button type="button" aria-pressed={pressed("keep")} disabled={busy} onClick={() => onDecide("keep")}
          className={buttonClass("secondary", "touch", clsx(pressed("keep") && "border-ok bg-ok-soft text-ok"))}>{t("p.review.keep")}</button>
        <button type="button" aria-pressed={pressed("revoke")} disabled={busy} onClick={() => onDecide("revoke")}
          className={buttonClass("secondary", "touch", clsx(pressed("revoke") && "border-neutral bg-neutral-soft text-neutral"))}>{t("p.review.revoke")}</button>
        <Button variant="ghost" size="touch" disabled={busy} onClick={onReassign}>{it.snapshot.orphan ? t("p.review.assignOwner") : t("p.review.passOn")}</Button>
        {it.decision && <span className="ml-auto flex items-center gap-2 text-small text-fg-3"><StatusBadge domain="certification" status={it.decision} audience="portal" compact />{it.decidedAt ? fmt.day(it.decidedAt) : ""}</span>}
      </div>
    </li>
  );
}

export function ReviewCampaignPage() {
  const t = useT();
  const fmt = useFmt();
  const toast = useToast();
  const qc = useQueryClient();
  const me = useCurrentUser();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [params, setParams] = useSearchParams();
  const campaigns = useCampaigns(!!me?.identity);
  const campaign: Campaign | undefined = campaigns.data?.items.find((c) => c.id === campaignId);
  const res = useApi<Page<CertItem> & { progress: CampaignProgress }>(["cert-items", campaignId], `/api/portal/certifications/${campaignId}/items`, { decision: "all", page: 1, pageSize: 200 }, { enabled: !!me?.identity });
  const [busy, setBusy] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [reassign, setReassign] = useState<CertItem | null>(null);
  const [reassignTo, setReassignTo] = useState<Person | null>(null);

  const groups: PersonGroup[] = useMemo(() => {
    const m = new Map<string, PersonGroup>();
    for (const it of res.data?.items ?? []) { const g = m.get(it.identity.id) ?? { identity: it.identity, items: [], decided: 0 }; g.items.push(it); if (it.decision) g.decided++; m.set(it.identity.id, g); }
    return [...m.values()].sort((a, b) => (a.decided === a.items.length ? 1 : 0) - (b.decided === b.items.length ? 1 : 0) || a.identity.displayName.localeCompare(b.identity.displayName));
  }, [res.data]);
  const personParam = params.get("person");
  const person = groups.find((g) => g.identity.id === personParam) ?? null;
  const setPerson = (id: string | null) => setParams((p) => { const n = new URLSearchParams(p); if (id) n.set("person", id); else n.delete("person"); return n; }, { replace: true });
  useEffect(() => {
    if (person || groups.length === 0 || !window.matchMedia("(min-width: 768px)").matches) return;
    const first = groups.find((g) => g.decided < g.items.length) ?? groups[0]!;
    setPerson(first.identity.id);
  }, [groups, person]); // eslint-disable-line react-hooks/exhaustive-deps
  const next = person ? groups.find((g) => g.identity.id !== person.identity.id && g.decided < g.items.length) ?? null : groups.find((g) => g.decided < g.items.length) ?? null;
  const refresh = () => { for (const k of CERT_KEYS) void qc.invalidateQueries({ queryKey: k }); };

  async function decide(it: CertItem, d: Decision) {
    if (it.decision === d) return;
    setBusy(it.id);
    try {
      await apiFetch(`/api/portal/certifications/items/${it.id}/decide`, { method: "POST", json: { decision: d } });
      toast.push(t(d === "keep" ? "p.review.kept" : "p.review.revoked", { item: it.snapshot.subject.displayName }), { tone: "ok" });
      refresh();
    } catch (e) { toast.push(errMsg(e), { tone: "bad" }); }
    finally { setBusy(null); }
  }
  async function bulkKeep() {
    if (!person) return;
    const ids = person.items.filter(autoKeepable).map((i) => i.id);
    setBusy("bulk");
    try {
      const r = await apiFetch<{ results: { id: string; ok: boolean; error?: string }[]; decided: number }>(`/api/portal/certifications/${campaignId}/decide-bulk`, { method: "POST", json: { itemIds: ids, decision: "keep" } });
      const failed = r.results.filter((x) => !x.ok).length;
      toast.push(failed ? t("p.review.bulkKeptPartial", { count: r.decided, failed }) : t("p.review.bulkKept", { count: r.decided }), { tone: failed ? "warn" : "ok" });
      setBulkOpen(false); refresh();
    } catch (e) { toast.push(errMsg(e), { tone: "bad" }); }
    finally { setBusy(null); }
  }
  async function doReassign() {
    if (!reassign || !reassignTo) return;
    setBusy(reassign.id);
    try {
      await apiFetch(`/api/portal/certifications/items/${reassign.id}/decide`, { method: "POST", json: { decision: "reassign", reassignedTo: reassignTo.id } });
      toast.push(t(reassign.snapshot.orphan ? "p.review.ownerAssigned" : "p.review.passedOn", { name: reassignTo.displayName }), { tone: "ok" });
      setReassign(null); setReassignTo(null); refresh();
    } catch (e) { toast.push(errMsg(e), { tone: "bad" }); }
    finally { setBusy(null); }
  }

  if (!me?.identity) return <><PageHeader portal title={t("p.nav.reviews")} /><NoIdentity /></>;
  const p = res.data?.progress;
  const total = p?.total ?? 0, decided = p?.decided ?? 0;
  const keepable = person ? person.items.filter(autoKeepable).length : 0;
  const personDone = !!person && person.decided === person.items.length;
  const revoked = person?.items.filter((i) => i.decision === "revoke") ?? [];
  const isManagerReview = campaign?.type === "manager";

  return (
    <div className="flex flex-col gap-5">
      <div className={clsx(person && "hidden md:block")}>
        <PageHeader portal title={campaign ? t("p.review.title", { name: campaign.name }) : t("p.nav.reviews")} breadcrumb={<Link to="/tasks">{t("p.nav.tasks")}</Link>}
          subtitle={campaign && <>{t("p.review.purpose")} {t("p.review.dueSentence", { date: fmt.day(campaign.dueAt) })} {t("p.review.removedAtClose")}</>}
          actions={p && <div className="min-w-56"><div className="mb-1 text-small text-fg-2">{t("p.review.progress", { decided, total })}{total - decided > 0 ? ` · ${t("p.review.left", { count: total - decided })}` : ""}</div><ProgressBar value={decided} max={total} label={campaign?.name} /></div>} />
      </div>
      <LoadError error={res.error ?? campaigns.error} onRetry={() => void res.refetch()} />
      {res.isLoading ? <div className="rounded-card border border-line bg-surface p-4"><Skeleton rows={4} /></div> : groups.length === 0 ? <EmptyState kind="all-done" title={t("p.review.noItems")} body={t("p.review.noItemsBody")} /> : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-[280px_minmax(0,1fr)]">
          <nav className={clsx("rounded-card border border-line bg-surface self-start overflow-hidden", person && "hidden md:block")} aria-label={t("p.review.people")}>
            <div className="border-b border-line px-4 py-2.5 text-label text-fg-3">{isManagerReview ? t("p.review.yourTeam", { count: groups.length }) : t("p.review.peopleCount", { count: groups.length })}</div>
            <ul>
              {groups.map((g) => { const done = g.decided === g.items.length; const on = g.identity.id === person?.identity.id; return (
                <li key={g.identity.id}>
                  <button type="button" aria-current={on ? "true" : undefined} aria-label={done ? `${g.identity.displayName} · ${t("p.review.done")}` : `${g.identity.displayName} · ${g.decided}/${g.items.length}`} onClick={() => setPerson(g.identity.id)} className={clsx("flex min-h-14 w-full items-center gap-2.5 px-4 text-left hover:bg-surface-2", on && "bg-brand-soft")}>
                    <Avatar name={g.identity.displayName} size={32} />
                    <span className="min-w-0 flex-1 truncate font-semibold">{g.identity.displayName}</span>
                    {done ? <TonePill tone="ok" compact>{t("p.review.done")}</TonePill> : <span className="text-small font-semibold text-fg-2">{g.decided}/{g.items.length}</span>}
                  </button>
                </li>
              ); })}
            </ul>
          </nav>
          <div className={clsx("flex min-w-0 flex-col gap-4", !person && "hidden md:flex")}>
            {!person ? <div className="rounded-card border border-line bg-surface p-5 text-fg-3">{t("p.review.pickPerson")}</div> : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="touch" className="md:hidden" icon={<ArrowLeft className="h-4 w-4" aria-hidden="true" />} onClick={() => setPerson(null)} aria-label={t("ui.back")} />
                    <PersonLine person={person.identity} secondary={<>{person.identity.email}{person.identity.status !== "active" && <> · <StatusBadge domain="identity" status={person.identity.status} audience="portal" compact /></>}</>} size={40} />
                  </div>
                  {keepable > 0 && <Button variant="secondary" size="touch" onClick={() => setBulkOpen(true)}>{t("p.review.keepLowRisk", { count: keepable })}</Button>}
                </div>
                {person.identity.openViolations > 0 && <p className="text-small text-fg-2">{t("p.review.conflictNote", { name: firstName(person.identity.displayName), count: person.identity.openViolations })}</p>}
                {personDone && (
                  <section className="rounded-card border border-line bg-surface flex flex-col gap-2 border-ok p-5">
                    <h2 className="text-h2">{t("p.review.personDone", { name: firstName(person.identity.displayName) })}</h2>
                    <p className="text-fg-2">{t("p.review.summary", { kept: person.items.filter((i) => i.decision === "keep").length, revoked: revoked.length, passed: person.items.filter((i) => i.decision === "reassign").length })}</p>
                    {revoked.length > 0 && <>
                      <p className="font-semibold">{t("p.review.removedWhen", { date: campaign ? fmt.day(campaign.dueAt) : "" })}</p>
                      <ul className="list-disc pl-5 text-fg-2">{revoked.map((i) => <li key={i.id}>{i.snapshot.subject.displayName} <span className="text-fg-3">{i.snapshot.subject.application ?? i.snapshot.subject.system}</span></li>)}</ul>
                    </>}
                    <p className="text-small text-fg-3">{t("p.review.canChange")}</p>
                  </section>
                )}
                <ul className="flex flex-col gap-3">
                  {person.items.map((it) => <ItemCard key={it.id} it={it} busy={busy === it.id || busy === "bulk"} onDecide={(d) => void decide(it, d)} onReassign={() => { setReassign(it); setReassignTo(null); }} />)}
                </ul>
                <div className="sticky bottom-20 flex flex-wrap items-center justify-between gap-3 rounded-card border border-line bg-surface px-4 py-3 shadow-e1 md:bottom-4">
                  <span className="text-fg-2">{t("p.review.personProgress", { decided: person.decided, total: person.items.length, name: firstName(person.identity.displayName) })} · <span className="text-fg-3">{t("p.review.savedAsYouGo")}</span></span>
                  {next && <Button variant={personDone ? "primary" : "secondary"} size="touch" onClick={() => { setPerson(next.identity.id); window.scrollTo({ top: 0 }); }} icon={<ArrowRight className="h-4 w-4" aria-hidden="true" />}>{t("p.review.next", { name: next.identity.displayName, left: next.items.length - next.decided })}</Button>}
                  {!next && decided === total && total > 0 && <LinkButton to="/tasks" variant="primary">{t("p.review.allDone")}</LinkButton>}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <ConfirmDialog open={bulkOpen} onClose={() => setBulkOpen(false)} title={t("p.review.keepLowRisk", { count: keepable })} confirmLabel={t("p.review.keepN", { count: keepable })} keepLabel={t("p.review.decideOneByOne")} loading={busy === "bulk"}
        consequence={t("p.review.bulkBody", { name: firstName(person?.identity.displayName) })} onConfirm={() => void bulkKeep()} />
      <Dialog open={!!reassign} onClose={() => setReassign(null)} title={reassign?.snapshot.orphan ? t("p.review.assignOwner") : t("p.review.passOn")} size="sm"
        footer={<><Button variant="secondary" onClick={() => setReassign(null)}>{t("ui.cancel")}</Button><Button disabled={!reassignTo} loading={busy === reassign?.id} onClick={() => void doReassign()}>{reassign?.snapshot.orphan ? t("p.review.assignOwner") : t("p.review.passOn")}</Button></>}>
        {reassign && <div className="flex flex-col gap-3">
          <p className="text-fg-2"><span className="font-semibold text-fg">{reassign.snapshot.subject.displayName}</span> · {reassign.identity.displayName}</p>
          <Field label={reassign.snapshot.orphan ? t("p.review.ownerLabel") : t("p.review.reviewerLabel")} hint={reassign.snapshot.orphan ? t("p.review.ownerHint") : t("p.review.reviewerHint")} htmlFor="reassign-to">
            <PeoplePicker id="reassign-to" value={reassignTo} onChange={setReassignTo} autoFocus />
          </Field>
        </div>}
      </Dialog>
    </div>
  );
}
